import { useState, useCallback } from 'react';
import styles from './passcodeModal.module.scss';
import Modal from 'src/components/modal/Modal';
import Button from 'src/components/button/Button';
import Spinner from '@src/components/spinner/Spinner';
import LockIcon from '@src/assets/icons/locksimple.svg?react';
import { useAuthToken } from '@hooks/useAuthToken';
import { TextInput, TextInputValue } from '@src/components/text-input/TextInput';
import { isEmptyObject, validatePassword } from '@src/utils/validation';
import { useHeaSetPasswordAfterLoginMutation } from '@src/api/graphql-codegen';
import { showSuccessNotification } from '@src/hooks';

type Props = {
  open: boolean;
  onClose: () => void;
  studyplanId?: string;
};

const emptyErrors = {
  repassword: '',
  confirmPassword: '',
  verifyPassword: ''
};

function PasswordModal(props: Props) {
  const { open, studyplanId='0', onClose } = props;
  const [ repassword, setRePassword ] = useState('');
  const [ confirmPassword, setConfirmPassword ] = useState('');
  const [ beError, setBeError ] = useState('');
  const [errors, setErrors] = useState(emptyErrors);
  const { password: authPassword, setAuthPassword } = useAuthToken();
  const [ verified, setVerified ] = useState(authPassword ? false : true);
  const [ verifyPassword, setVerifyPassword ] = useState('');

  const [heaSetPasswordAfterLogin, { loading }] = useHeaSetPasswordAfterLoginMutation({
    onCompleted: () => {
      if (!authPassword) {
        showSuccessNotification('Password has been created successfully!');
      } else {
        showSuccessNotification('Password has been updated successfully!');
      }

      setAuthPassword(confirmPassword)
      onClose();
    },
    onError: (e) => {
      setBeError(e.message);
    }
  });

  const handleClose = () => {
    onClose();
  };

  const handleSave = useCallback(() => {
    const errors: any = {};

    if (verified) {
      let p = validatePassword(repassword)
      if (p) {
        errors.repassword = p;
      }

      p = validatePassword(confirmPassword)
      if (p) {
        errors.confirmPassword = p;
      }
    }
    if (!isEmptyObject(errors)) {
      setErrors(errors)
      return
    }

    if (!verified) {
      if (authPassword !== verifyPassword) {
        setBeError('Incorrect Password. Please try again');
        return;
      }
      setBeError('');
      setErrors(emptyErrors);
      setVerified(true);
      return;
    }
    if (repassword && confirmPassword) {
      if (repassword !== confirmPassword) {
        setBeError('Password and confirm password should be equal.');
        return;
      }
    }
    setBeError('');
    setErrors(emptyErrors);
    if (repassword) {
      heaSetPasswordAfterLogin({
        variables: {
          input: {
            password: repassword,
            studyplanId: studyplanId,
          }
        }
      });
    }
  }, [setBeError, setErrors, verified, authPassword, verifyPassword, repassword, confirmPassword, studyplanId, heaSetPasswordAfterLogin])

  const handleChangeText = useCallback((setter, key: keyof typeof emptyErrors) => (value: TextInputValue) => {
    setErrors({
      ...errors,
      [key]: '',
    });
    setBeError('');
    setter(value)
  }, [errors]);

  return (
    <Modal open={open} onClose={handleClose} className={styles.passwordWrapper}>
      <div>
        {verified ? <div className={styles.headView}>
          <LockIcon width={44} height={44} />
          <div className={styles.title}>{!authPassword ? 'Create your Password' : 'Change your Password'}</div>
        </div> : <>
          <LockIcon width={44} height={44} />
          <div className={styles.title}>{'Please re-type your password:'}</div>
        </>}
        <div className={styles.passwordContent}>
          {verified ? <>
            <TextInput allowSpecialCharacters id="password" hasError={!!errors.repassword} subTitle={errors.repassword} type="password"  placeholder={'Type your password'} value={repassword} label={''} onChange={handleChangeText(setRePassword, 'repassword')} />
            <TextInput allowSpecialCharacters id="confirmpassword" hasError={!!errors.confirmPassword} subTitle={errors.confirmPassword} type="password"  placeholder={'Re-type your password'} value={confirmPassword} label={''} onChange={handleChangeText(setConfirmPassword, 'confirmPassword')} />
          </> : <TextInput allowSpecialCharacters id="verifyPassword" hasError={!!errors.verifyPassword} subTitle={errors.verifyPassword} type="password"  placeholder={'Type your password'} value={verifyPassword} label={''} onChange={handleChangeText(setVerifyPassword, 'verifyPassword')} />}
        </div>
        {Boolean(beError) && <div className={styles.error}>{beError}</div>}
        <div className={styles.buttonAction}>
          <Button disabled={verified ? (!repassword || !confirmPassword || loading) : !verifyPassword} size="small" leftIcon={loading ? <Spinner width={20} height={20} /> : undefined} className={styles.okButton} onClick={handleSave} label={!authPassword ? 'Set Password': verified ? 'Reset Password' : 'Please type your existing password'}/>
        </div>
      </div>
    </Modal>
  );
}

export default PasswordModal;
